import * as React from "react";

import type { Table } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

import { useIsMobile } from "../../../lib/use-mobile";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Select } from "../../ui/select";
import { DataTableLoadMore, useDataTableMobileList } from "./data-table-mobile-list";

/**
 * The page sizes every table offers unless it overrides them.
 *
 * ONE definition on purpose: this list was hard-coded in four separate places, so adding a size meant finding
 * all four — and missing one meant a table that offered a page size its footer refused to show.
 *
 * Note that the smallest option is also the footer's visibility threshold (see
 * `shouldShowDataTablePaginationFooter`): a pager is pointless when every row already fits on one page, so it
 * appears only once the row count exceeds the smallest size a user could pick.
 */
export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100, 200];

type DataTablePaginationBaseProps = {
  pageSizeOptions?: number[];
};

type DataTableLocalPaginationProps<TData> = DataTablePaginationBaseProps & {
  table: Table<TData>;
  server?: never;
  summaryText?: never;
};

export type DataTableServerPaginationConfig = {
  page: number;
  pageSize: number;
  totalCount?: number;
  totalPages?: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onFirstPage?: () => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onLastPage?: () => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** When true, omit the rows-per-page control (e.g. fixed server page size). */
  hidePageSizeSelect?: boolean;
};

type DataTableServerPaginationProps = DataTablePaginationBaseProps & {
  server: DataTableServerPaginationConfig;
  table?: never;
  summaryText?: string;
};

type DataTablePaginationProps<TData> = DataTableLocalPaginationProps<TData> | DataTableServerPaginationProps;

function isServerPaginationProps<TData>(
  props: DataTablePaginationProps<TData>,
): props is DataTableServerPaginationProps {
  return "server" in props;
}

/**
 * Pagination is pointless until there are strictly more rows than the smallest selectable page size.
 * When `totalRows` is undefined (e.g. server total not resolved yet), the footer stays visible so layout does not flicker.
 */
export function shouldShowDataTablePaginationFooter(pageSizeOptions: number[], totalRows: number | undefined): boolean {
  if (typeof totalRows !== "number") return true;
  const opts = pageSizeOptions.length > 0 ? pageSizeOptions : DEFAULT_PAGE_SIZE_OPTIONS;
  const smallestPageSizeChoice = Math.min(...opts);
  return totalRows > smallestPageSizeChoice;
}

type MobileNavButtonProps = {
  show?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel: string;
  children: React.ReactNode;
};

function MobileNavButton({ show = true, onClick, disabled, ariaLabel, children }: MobileNavButtonProps) {
  if (!show) {
    return null;
  }

  return (
    <Button
      variant='outline'
      size='icon-sm'
      className='shrink-0'
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </Button>
  );
}

function MobilePaginationNav({
  pageLabel,
  first,
  previous,
  next,
  last,
}: {
  pageLabel: string;
  first: Omit<MobileNavButtonProps, "children"> & { icon: React.ReactNode };
  previous: Omit<MobileNavButtonProps, "children"> & { icon: React.ReactNode };
  next: Omit<MobileNavButtonProps, "children"> & { icon: React.ReactNode };
  last: Omit<MobileNavButtonProps, "children"> & { icon: React.ReactNode };
}) {
  return (
    <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-4'>
      <div className='flex items-center justify-start gap-3'>
        <MobileNavButton show={first.show} onClick={first.onClick} disabled={first.disabled} ariaLabel={first.ariaLabel}>
          {first.icon}
        </MobileNavButton>
        <MobileNavButton
          show={previous.show}
          onClick={previous.onClick}
          disabled={previous.disabled}
          ariaLabel={previous.ariaLabel}
        >
          {previous.icon}
        </MobileNavButton>
      </div>

      <div
        className={cn(
          "bg-muted/40 text-foreground min-w-[4.25rem] rounded-full border border-border/60 px-2.5 py-1 text-center",
          "text-xs font-medium tabular-nums",
        )}
      >
        {pageLabel}
      </div>

      <div className='flex items-center justify-end gap-3'>
        <MobileNavButton show={next.show} onClick={next.onClick} disabled={next.disabled} ariaLabel={next.ariaLabel}>
          {next.icon}
        </MobileNavButton>
        <MobileNavButton show={last.show} onClick={last.onClick} disabled={last.disabled} ariaLabel={last.ariaLabel}>
          {last.icon}
        </MobileNavButton>
      </div>
    </div>
  );
}

export function DataTablePagination<TData>(props: DataTablePaginationProps<TData>) {
  const pageSizeOptions = props.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  const isMobile = useIsMobile();
  /**
   * MEMOISED, and load-bearing.
   *
   * This array is handed straight to `Select`, whose option pipeline is a chain of `useMemo`s keyed on it, and
   * whose menu attaches ref callbacks derived from that chain. Rebuilding it every render gave every link new
   * identities, so the refs detached and reattached on each pass — and a ref that dispatches state turns that
   * into render -> ref -> setState -> render, which React eventually kills with "Maximum update depth
   * exceeded", thrown from inside `composeRefs` where no component of ours appears in the stack.
   *
   * `pageSizeOptions` is a module-level constant unless a caller overrides it, so this memo is stable in
   * practice; the join keeps it honest for a caller that does pass a fresh array.
   */
  const pageSizeSelectOptions = React.useMemo(
    () => pageSizeOptions.map((size) => ({ value: String(size), label: String(size) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageSizeOptions.join(",")],
  );

  // Inside a phone list that grows in place, the server pager becomes "Load more" and reports its page so the
  // table keeps the rows already shown.
  const mobileList = useDataTableMobileList();
  const serverPage = isServerPaginationProps(props) ? props.server.page : null;
  const loadMoreMode = isMobile && Boolean(mobileList?.loadMore) && serverPage !== null;
  const reportServerPage = mobileList?.reportServerPage;
  React.useEffect(() => {
    if (loadMoreMode && serverPage !== null) reportServerPage?.(serverPage);
  }, [loadMoreMode, reportServerPage, serverPage]);

  // While the next page loads, callers often report no total yet; keep the last one rather than showing "40 of 0".
  const lastTotalRef = React.useRef<number | undefined>(undefined);
  const loadingNextPage = Boolean(
    loadMoreMode && mobileList && serverPage !== null && mobileList.loadedPage !== null && mobileList.loadedPage < serverPage,
  );
  const reportedTotal = isServerPaginationProps(props) ? props.server.totalCount : undefined;
  if (!loadingNextPage && typeof reportedTotal === "number" && reportedTotal > 0) lastTotalRef.current = reportedTotal;

  if (loadMoreMode && mobileList && isServerPaginationProps(props) && serverPage !== null) {
    return (
      <DataTableLoadMore
        shown={mobileList.shownCount}
        total={loadingNextPage ? (lastTotalRef.current ?? reportedTotal) : reportedTotal}
        canLoadMore={loadingNextPage || props.server.canNextPage}
        loading={loadingNextPage}
        onLoadMore={props.server.onNextPage}
      />
    );
  }

  const totalRowsKnown: number | undefined = isServerPaginationProps(props)
    ? props.server.totalCount
    : props.table.getFilteredRowModel().rows.length;

  if (!shouldShowDataTablePaginationFooter(pageSizeOptions, totalRowsKnown)) {
    return null;
  }

  if (isServerPaginationProps(props)) {
    const hidePageSizeSelect = Boolean(props.server.hidePageSizeSelect);
    const totalPages = Math.max(props.server.totalPages ?? props.server.page, 1);
    const totalCount = props.server.totalCount;
    const rangeSummary =
      typeof totalCount === "number"
        ? totalCount > 0
          ? `${(props.server.page - 1) * props.server.pageSize + 1}-${Math.min(props.server.page * props.server.pageSize, totalCount)} of ${totalCount}`
          : "0-0 of 0"
        : props.summaryText ?? "";

    if (isMobile) {
      return (
        <div className='flex min-h-16 flex-col gap-2 px-3 pt-3 pb-2'>
          <div className='flex items-center justify-between gap-2'>
            <div className='text-muted-foreground min-w-0 truncate text-xs'>{rangeSummary}</div>
            {!hidePageSizeSelect ? (
              <div className='flex shrink-0 items-center gap-1.5'>
                <span className='text-muted-foreground text-xs'>Show</span>
                <Select
                  value={String(props.server.pageSize)}
                  onValueChange={(value) => props.server.onPageSizeChange?.(Number(value))}
                  disabled={!props.server.onPageSizeChange}
                  options={pageSizeSelectOptions}
                  className='w-[68px]'
                  size='sm'
                />
              </div>
            ) : null}
          </div>

          <MobilePaginationNav
            pageLabel={`${props.server.page}/${totalPages}`}
            first={{
              show: Boolean(props.server.onFirstPage),
              onClick: props.server.onFirstPage,
              disabled: !props.server.canPreviousPage,
              ariaLabel: "First page",
              icon: <ChevronsLeft className='size-4' />,
            }}
            previous={{
              onClick: props.server.onPreviousPage,
              disabled: !props.server.canPreviousPage,
              ariaLabel: "Previous page",
              icon: <ChevronLeft className='size-4' />,
            }}
            next={{
              onClick: props.server.onNextPage,
              disabled: !props.server.canNextPage,
              ariaLabel: "Next page",
              icon: <ChevronRight className='size-4' />,
            }}
            last={{
              show: Boolean(props.server.onLastPage),
              onClick: props.server.onLastPage,
              disabled: !props.server.canNextPage,
              ariaLabel: "Last page",
              icon: <ChevronsRight className='size-4' />,
            }}
          />
        </div>
      );
    }

    return (
      <div className='flex min-h-12 flex-wrap items-center gap-3 px-6 pt-2 pb-2'>
        <div className='text-muted-foreground min-w-0 flex-1 text-sm'>{rangeSummary}</div>

        {!hidePageSizeSelect ? (
          <div className='flex shrink-0 items-center gap-2'>
            <span className='text-muted-foreground whitespace-nowrap text-sm'>Rows per page</span>
            <Select
              value={String(props.server.pageSize)}
              onValueChange={(value) => props.server.onPageSizeChange?.(Number(value))}
              disabled={!props.server.onPageSizeChange}
              options={pageSizeSelectOptions}
              className='w-[88px] min-w-[88px]'
              size='sm'
            />
          </div>
        ) : null}

        <div className='text-muted-foreground shrink-0 whitespace-nowrap text-sm'>
          {`Page ${props.server.page} of ${totalPages}`}
        </div>

        <Button
          variant='outline'
          size='icon-sm'
          onClick={props.server.onPreviousPage}
          disabled={!props.server.canPreviousPage}
          aria-label='Previous page'
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant='outline'
          size='icon-sm'
          onClick={props.server.onNextPage}
          disabled={!props.server.canNextPage}
          aria-label='Next page'
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    );
  }

  const table = props.table;
  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const totalRows = table.getFilteredRowModel().rows.length;
  const totalPages = Math.max(table.getPageCount(), 1);
  const rangeSummary = totalRows > 0 ? `${pageIndex * pageSize + 1}-${Math.min((pageIndex + 1) * pageSize, totalRows)} of ${totalRows}` : "0-0 of 0";

  if (isMobile) {
    return (
      <div className='flex min-h-16 flex-col gap-2 px-3 pt-3 pb-2'>
        <div className='flex items-center justify-between gap-2'>
          <div className='text-muted-foreground min-w-0 truncate text-xs'>{rangeSummary}</div>
          <div className='flex shrink-0 items-center gap-1.5'>
            <span className='text-muted-foreground text-xs'>Show</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => table.setPageSize(Number(value))}
              options={pageSizeSelectOptions}
              className='w-[68px]'
              size='sm'
            />
          </div>
        </div>

        <MobilePaginationNav
          pageLabel={`${pageIndex + 1}/${totalPages}`}
          first={{
            onClick: () => table.setPageIndex(0),
            disabled: !table.getCanPreviousPage(),
            ariaLabel: "First page",
            icon: <ChevronsLeft className='size-4' />,
          }}
          previous={{
            onClick: () => table.previousPage(),
            disabled: !table.getCanPreviousPage(),
            ariaLabel: "Previous page",
            icon: <ChevronLeft className='size-4' />,
          }}
          next={{
            onClick: () => table.nextPage(),
            disabled: !table.getCanNextPage(),
            ariaLabel: "Next page",
            icon: <ChevronRight className='size-4' />,
          }}
          last={{
            onClick: () => table.setPageIndex(Math.max(table.getPageCount() - 1, 0)),
            disabled: !table.getCanNextPage(),
            ariaLabel: "Last page",
            icon: <ChevronsRight className='size-4' />,
          }}
        />
      </div>
    );
  }

  return (
    <div className='flex min-h-12 flex-wrap items-center gap-3 px-6 pt-2 pb-2'>
      <div className='text-muted-foreground min-w-0 flex-1 text-sm'>{rangeSummary}</div>

      <div className='flex shrink-0 items-center gap-2'>
        <span className='text-muted-foreground whitespace-nowrap text-sm'>Rows per page</span>
        <Select
          value={String(table.getState().pagination.pageSize)}
          onValueChange={(value) => table.setPageSize(Number(value))}
          options={pageSizeSelectOptions}
          className='w-[88px] min-w-[88px]'
          size='sm'
        />
      </div>

      <div className='text-muted-foreground shrink-0 whitespace-nowrap text-sm'>
        {`Page ${table.getState().pagination.pageIndex + 1} of ${Math.max(table.getPageCount(), 1)}`}
      </div>

      <Button
        variant='outline'
        size='icon-sm'
        onClick={() => table.previousPage()}
        disabled={!table.getCanPreviousPage()}
        aria-label='Previous page'
      >
        <ChevronLeft className="size-4" />
      </Button>
      <Button
        variant='outline'
        size='icon-sm'
        onClick={() => table.nextPage()}
        disabled={!table.getCanNextPage()}
        aria-label='Next page'
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );
}
